import { useState } from 'react'
import {
  Rocket, Github, Database, Globe, ChevronRight,
  Plus, CheckCircle, Clock, AlertCircle, XCircle,
  RefreshCw, ArrowRight, ArrowUpRight, RotateCcw, Settings,
  FileCode, Layers, Shield, Activity, Zap, Cloud, Container,
  HardDrive, Eye, X, TriangleAlert,
} from 'lucide-react'
import styles from './OneClickDeployPage.module.css'

// ─── Types ────────────────────────────────────────────────────────────────────

type AppType    = 'auto' | 'nodejs' | 'python' | 'java' | 'dotnet' | 'go' | 'static'
type Target     = 'container-apps' | 'aks'
type DbOption   = 'new' | 'existing'
type EnvName    = 'dev' | 'qa' | 'demo' | 'uat' | 'prod'
type DeployStatus = 'running' | 'healthy' | 'failed' | 'deploying' | 'stopped'

interface EnvVar { key: string; value: string; secret: boolean }

interface WizardState {
  // Step 1
  repoUrl: string; branch: string; subfolder: string
  dockerfilePath: string; appType: AppType
  // Step 2
  target: Target; aksCluster: string; region: string
  cpuCores: string; memoryGb: string
  minReplicas: number; maxReplicas: number; customDomain: string
  // Step 3
  dbOption: DbOption
  dbServerName: string; dbRegion: string; dbSku: string
  dbHighAvailability: boolean; dbStorageGb: number
  dbBackupDays: number; dbSslEnforcement: boolean
  dbHost: string; dbPort: string; dbName: string
  dbUser: string; dbPassword: string; dbSslMode: string
  // Step 4
  environment: EnvName; envVars: EnvVar[]
}

interface Application {
  id: string; name: string; repo: string; branch: string
  target: Target; environment: EnvName; status: DeployStatus
  appType: AppType; region: string; lastDeployed: string
  image: string; version: string
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_APPS: Application[] = [
  { id: '1', name: 'customer-portal', repo: 'github.com/aptean/customer-portal', branch: 'main', target: 'container-apps', environment: 'prod', status: 'healthy', appType: 'nodejs', region: 'East US', lastDeployed: '2h ago', image: 'acreastus.azurecr.io/customer-portal', version: 'v2.4.1' },
  { id: '2', name: 'billing-api', repo: 'github.com/aptean/billing-service', branch: 'main', target: 'aks', environment: 'uat', status: 'healthy', appType: 'python', region: 'West Europe', lastDeployed: '1d ago', image: 'acreastus.azurecr.io/billing-api', version: 'v1.8.0' },
  { id: '3', name: 'analytics-engine', repo: 'github.com/aptean/analytics', branch: 'develop', target: 'container-apps', environment: 'qa', status: 'deploying', appType: 'java', region: 'East US', lastDeployed: 'Just now', image: 'acreastus.azurecr.io/analytics', version: 'v3.1.0' },
  { id: '4', name: 'notification-svc', repo: 'github.com/aptean/notify', branch: 'main', target: 'aks', environment: 'dev', status: 'failed', appType: 'go', region: 'North Europe', lastDeployed: '3h ago', image: 'acreastus.azurecr.io/notify', version: 'v0.9.2' },
  { id: '5', name: 'erp-frontend', repo: 'github.com/aptean/erp-ui', branch: 'release/v5', target: 'container-apps', environment: 'demo', status: 'healthy', appType: 'static', region: 'East US', lastDeployed: '5d ago', image: 'acreastus.azurecr.io/erp-ui', version: 'v5.0.0' },
]

const AKS_CLUSTERS = [
  'aks-eastus-prod-nextgen',
  'aks-westeurope-prod-nextgen',
  'aks-eastus-dev-nextgen',
  'pe-platformcentral-dev',
]

const REGIONS = [
  'East US', 'East US 2', 'West US 2', 'West Europe',
  'North Europe', 'UK South', 'Southeast Asia', 'Australia East',
]

const DB_SKUS = [
  'Burstable B1ms (1 vCore, 2 GiB)',
  'General Purpose D2s v3 (2 vCores, 8 GiB)',
  'General Purpose D4s v3 (4 vCores, 16 GiB)',
  'Memory Optimized E4ds v4 (4 vCores, 32 GiB)',
  'Memory Optimized E8ds v4 (8 vCores, 64 GiB)',
]

const ENV_ORDER: EnvName[] = ['dev', 'qa', 'demo', 'uat', 'prod']

const STATUS_META: Record<DeployStatus, { label: string; color: string; Icon: React.ElementType }> = {
  healthy:   { label: 'Healthy',   color: 'var(--aptean-success)', Icon: CheckCircle },
  running:   { label: 'Running',   color: 'var(--aptean-teal)',    Icon: Activity },
  deploying: { label: 'Deploying', color: 'var(--aptean-warning)', Icon: Clock },
  failed:    { label: 'Failed',    color: 'var(--aptean-error)',   Icon: XCircle },
  stopped:   { label: 'Stopped',   color: 'var(--aptean-text-muted)', Icon: AlertCircle },
}

const APP_TYPE_LABELS: Record<AppType, string> = {
  auto: 'Auto-detect', nodejs: 'Node.js', python: 'Python',
  java: 'Java', dotnet: '.NET', go: 'Go', static: 'Static Site',
}

const STEPS = [
  { n: 1, label: 'Source',      Icon: Github   },
  { n: 2, label: 'Target',      Icon: Cloud    },
  { n: 3, label: 'Database',    Icon: Database },
  { n: 4, label: 'Environment', Icon: Settings },
  { n: 5, label: 'Review',      Icon: Rocket   },
]

const DEFAULT_WIZARD: WizardState = {
  repoUrl: '', branch: 'main', subfolder: '', dockerfilePath: 'Dockerfile', appType: 'auto',
  target: 'container-apps', aksCluster: '', region: 'East US',
  cpuCores: '0.5', memoryGb: '1', minReplicas: 1, maxReplicas: 5, customDomain: '',
  dbOption: 'new',
  dbServerName: '', dbRegion: 'East US', dbSku: DB_SKUS[1],
  dbHighAvailability: false, dbStorageGb: 32, dbBackupDays: 7, dbSslEnforcement: true,
  dbHost: '', dbPort: '5432', dbName: '', dbUser: '', dbPassword: '', dbSslMode: 'require',
  environment: 'dev',
  envVars: [{ key: 'NODE_ENV', value: 'production', secret: false }],
}

// ─── Helper components ────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: DeployStatus }) {
  const { label, color, Icon } = STATUS_META[status]
  return (
    <span className={styles.statusBadge} style={{ color, borderColor: `${color}40` }}>
      <Icon size={11} /> {label}
    </span>
  )
}

function EnvBadge({ env }: { env: EnvName }) {
  const colors: Record<EnvName, string> = {
    dev: '#3B82F6', qa: '#8B5CF6', demo: '#F59E0B',
    uat: '#F97316', prod: '#EF4444',
  }
  return (
    <span className={styles.envBadge} style={{ background: `${colors[env]}22`, color: colors[env], borderColor: `${colors[env]}44` }}>
      {env.toUpperCase()}
    </span>
  )
}

function TargetBadge({ target }: { target: Target }) {
  return (
    <span className={styles.targetBadge}>
      {target === 'container-apps' ? <><Cloud size={11} /> Container Apps</> : <><Container size={11} /> AKS</>}
    </span>
  )
}

// ─── Wizard Steps ─────────────────────────────────────────────────────────────

function Step1({ w, set }: { w: WizardState; set: (p: Partial<WizardState>) => void }) {
  const repoName = w.repoUrl.split('/').pop()?.replace('.git', '') || ''
  return (
    <div className={styles.stepBody}>
      <div className={styles.stepSection}>
        <div className={styles.stepSectionTitle}><Github size={14} /> Repository</div>
        <div className={styles.formGrid}>
          <div className={styles.formField} style={{ gridColumn: '1/-1' }}>
            <label>GitHub Repository URL *</label>
            <input value={w.repoUrl} onChange={e => set({ repoUrl: e.target.value })}
              placeholder="https://github.com/org/repository" />
          </div>
          <div className={styles.formField}>
            <label>Branch</label>
            <input value={w.branch} onChange={e => set({ branch: e.target.value })} placeholder="main" />
          </div>
          <div className={styles.formField}>
            <label>Subfolder Path <span className={styles.optional}>(optional)</span></label>
            <input value={w.subfolder} onChange={e => set({ subfolder: e.target.value })} placeholder="e.g. backend/" />
          </div>
          <div className={styles.formField}>
            <label>Dockerfile Path</label>
            <input value={w.dockerfilePath} onChange={e => set({ dockerfilePath: e.target.value })} placeholder="Dockerfile" />
          </div>
          <div className={styles.formField}>
            <label>Application Type</label>
            <select value={w.appType} onChange={e => set({ appType: e.target.value as AppType })}>
              {(Object.entries(APP_TYPE_LABELS) as [AppType, string][]).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {repoName && (
        <div className={styles.infoBox}>
          <FileCode size={14} />
          <span>Repository <strong>{repoName}</strong> on branch <strong>{w.branch}</strong> will be cloned, app type auto-detected, and a Dockerfile generated if not present. The image will be pushed to ACR.</span>
        </div>
      )}
    </div>
  )
}

function Step2({ w, set }: { w: WizardState; set: (p: Partial<WizardState>) => void }) {
  return (
    <div className={styles.stepBody}>
      <div className={styles.stepSection}>
        <div className={styles.stepSectionTitle}><Cloud size={14} /> Deployment Target</div>
        <div className={styles.targetCards}>
          <button
            className={`${styles.targetCard} ${w.target === 'container-apps' ? styles.targetCardActive : ''}`}
            onClick={() => set({ target: 'container-apps' })}
          >
            <Cloud size={28} />
            <div className={styles.targetCardTitle}>Azure Container Apps</div>
            <div className={styles.targetCardDesc}>Fully managed serverless containers. Auto-provisioned environment, ingress, scaling, and secrets.</div>
            <ul className={styles.targetCardFeatures}>
              <li><CheckCircle size={11} /> Auto-scaling to zero</li>
              <li><CheckCircle size={11} /> Built-in HTTPS</li>
              <li><CheckCircle size={11} /> Revision management</li>
              <li><CheckCircle size={11} /> No cluster management</li>
            </ul>
          </button>
          <button
            className={`${styles.targetCard} ${w.target === 'aks' ? styles.targetCardActive : ''}`}
            onClick={() => set({ target: 'aks' })}
          >
            <Container size={28} />
            <div className={styles.targetCardTitle}>Existing AKS Cluster</div>
            <div className={styles.targetCardDesc}>Deploy to an existing AKS cluster with full Kubernetes control, Helm charts, and blue/green support.</div>
            <ul className={styles.targetCardFeatures}>
              <li><CheckCircle size={11} /> Blue/Green & Canary</li>
              <li><CheckCircle size={11} /> Full K8s control</li>
              <li><CheckCircle size={11} /> Helm chart deployment</li>
              <li><CheckCircle size={11} /> Namespace isolation</li>
            </ul>
          </button>
        </div>
      </div>

      {w.target === 'container-apps' && (
        <div className={styles.stepSection}>
          <div className={styles.stepSectionTitle}><Settings size={14} /> Container App Configuration</div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label>Region</label>
              <select value={w.region} onChange={e => set({ region: e.target.value })}>
                {REGIONS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className={styles.formField}>
              <label>CPU Cores</label>
              <select value={w.cpuCores} onChange={e => set({ cpuCores: e.target.value })}>
                {['0.25', '0.5', '0.75', '1', '1.5', '2', '4'].map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div className={styles.formField}>
              <label>Memory (GiB)</label>
              <select value={w.memoryGb} onChange={e => set({ memoryGb: e.target.value })}>
                {['0.5', '1', '1.5', '2', '3', '4', '8'].map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div className={styles.formField}>
              <label>Min Replicas</label>
              <input type="number" min={0} max={10} value={w.minReplicas}
                onChange={e => set({ minReplicas: +e.target.value })} />
            </div>
            <div className={styles.formField}>
              <label>Max Replicas</label>
              <input type="number" min={1} max={100} value={w.maxReplicas}
                onChange={e => set({ maxReplicas: +e.target.value })} />
            </div>
            <div className={styles.formField}>
              <label>Custom Domain <span className={styles.optional}>(optional)</span></label>
              <input value={w.customDomain} onChange={e => set({ customDomain: e.target.value })}
                placeholder="app.yourdomain.com" />
            </div>
          </div>
        </div>
      )}

      {w.target === 'aks' && (
        <div className={styles.stepSection}>
          <div className={styles.stepSectionTitle}><Container size={14} /> AKS Configuration</div>
          <div className={styles.formGrid}>
            <div className={styles.formField} style={{ gridColumn: '1/-1' }}>
              <label>AKS Cluster</label>
              <select value={w.aksCluster} onChange={e => set({ aksCluster: e.target.value })}>
                <option value="">— Select cluster —</option>
                {AKS_CLUSTERS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className={styles.formField}>
              <label>Min Replicas</label>
              <input type="number" min={1} value={w.minReplicas}
                onChange={e => set({ minReplicas: +e.target.value })} />
            </div>
            <div className={styles.formField}>
              <label>Max Replicas</label>
              <input type="number" min={1} value={w.maxReplicas}
                onChange={e => set({ maxReplicas: +e.target.value })} />
            </div>
          </div>
          <div className={styles.infoBox}>
            <Layers size={14} />
            <span>A dedicated namespace will be created per environment. Helm chart, Ingress, HPA, ConfigMap, and Secrets will be auto-generated and deployed.</span>
          </div>
        </div>
      )}
    </div>
  )
}

function Step3({ w, set }: { w: WizardState; set: (p: Partial<WizardState>) => void }) {
  return (
    <div className={styles.stepBody}>
      <div className={styles.stepSection}>
        <div className={styles.stepSectionTitle}><Database size={14} /> PostgreSQL Configuration</div>
        <div className={styles.dbOptionCards}>
          <button
            className={`${styles.dbCard} ${w.dbOption === 'new' ? styles.dbCardActive : ''}`}
            onClick={() => set({ dbOption: 'new' })}
          >
            <Plus size={20} />
            <div className={styles.dbCardTitle}>Create New PostgreSQL</div>
            <div className={styles.dbCardDesc}>Provision a new Azure PostgreSQL Flexible Server automatically. Credentials stored in Key Vault.</div>
          </button>
          <button
            className={`${styles.dbCard} ${w.dbOption === 'existing' ? styles.dbCardActive : ''}`}
            onClick={() => set({ dbOption: 'existing' })}
          >
            <HardDrive size={20} />
            <div className={styles.dbCardTitle}>Use Existing Database</div>
            <div className={styles.dbCardDesc}>Connect to an existing PostgreSQL instance by providing connection details. Secrets stored in Key Vault.</div>
          </button>
        </div>
      </div>

      {w.dbOption === 'new' && (
        <div className={styles.stepSection}>
          <div className={styles.stepSectionTitle}><Settings size={14} /> New PostgreSQL Flexible Server</div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label>Server Name *</label>
              <input value={w.dbServerName} onChange={e => set({ dbServerName: e.target.value })}
                placeholder="psql-myapp-dev" />
            </div>
            <div className={styles.formField}>
              <label>Region</label>
              <select value={w.dbRegion} onChange={e => set({ dbRegion: e.target.value })}>
                {REGIONS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className={styles.formField} style={{ gridColumn: '1/-1' }}>
              <label>SKU / Tier</label>
              <select value={w.dbSku} onChange={e => set({ dbSku: e.target.value })}>
                {DB_SKUS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className={styles.formField}>
              <label>Storage (GiB)</label>
              <select value={w.dbStorageGb} onChange={e => set({ dbStorageGb: +e.target.value })}>
                {[32, 64, 128, 256, 512, 1024].map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div className={styles.formField}>
              <label>Backup Retention (days)</label>
              <select value={w.dbBackupDays} onChange={e => set({ dbBackupDays: +e.target.value })}>
                {[7, 14, 21, 28, 35].map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div className={styles.formField}>
              <label className={styles.checkLabel}>
                <input type="checkbox" checked={w.dbHighAvailability}
                  onChange={e => set({ dbHighAvailability: e.target.checked })} />
                High Availability (Zone-redundant)
              </label>
            </div>
            <div className={styles.formField}>
              <label className={styles.checkLabel}>
                <input type="checkbox" checked={w.dbSslEnforcement}
                  onChange={e => set({ dbSslEnforcement: e.target.checked })} />
                Enforce SSL
              </label>
            </div>
          </div>
          <div className={styles.infoBox}>
            <Shield size={14} />
            <span>Database name, user, and a strong password will be auto-generated. Credentials will be stored in Azure Key Vault and injected as secrets into your application.</span>
          </div>
        </div>
      )}

      {w.dbOption === 'existing' && (
        <div className={styles.stepSection}>
          <div className={styles.stepSectionTitle}><Settings size={14} /> Existing Database Connection</div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label>Hostname *</label>
              <input value={w.dbHost} onChange={e => set({ dbHost: e.target.value })}
                placeholder="psql-server.postgres.database.azure.com" />
            </div>
            <div className={styles.formField}>
              <label>Port</label>
              <input value={w.dbPort} onChange={e => set({ dbPort: e.target.value })} placeholder="5432" />
            </div>
            <div className={styles.formField}>
              <label>Database Name *</label>
              <input value={w.dbName} onChange={e => set({ dbName: e.target.value })} placeholder="myapp_db" />
            </div>
            <div className={styles.formField}>
              <label>Username *</label>
              <input value={w.dbUser} onChange={e => set({ dbUser: e.target.value })} placeholder="dbadmin" />
            </div>
            <div className={styles.formField}>
              <label>Password *</label>
              <input type="password" value={w.dbPassword} onChange={e => set({ dbPassword: e.target.value })} />
            </div>
            <div className={styles.formField}>
              <label>SSL Mode</label>
              <select value={w.dbSslMode} onChange={e => set({ dbSslMode: e.target.value })}>
                {['require', 'verify-full', 'prefer', 'disable'].map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className={styles.infoBox}>
            <Shield size={14} />
            <span>Connection details will be stored securely in Azure Key Vault and injected as environment secrets — never stored in source code.</span>
          </div>
        </div>
      )}
    </div>
  )
}

function Step4({ w, set }: { w: WizardState; set: (p: Partial<WizardState>) => void }) {
  function addVar() { set({ envVars: [...w.envVars, { key: '', value: '', secret: false }] }) }
  function removeVar(i: number) { set({ envVars: w.envVars.filter((_, idx) => idx !== i) }) }
  function updateVar(i: number, patch: Partial<EnvVar>) {
    set({ envVars: w.envVars.map((v, idx) => idx === i ? { ...v, ...patch } : v) })
  }

  const ENV_COLORS: Record<EnvName, string> = {
    dev: '#3B82F6', qa: '#8B5CF6', demo: '#F59E0B', uat: '#F97316', prod: '#EF4444',
  }

  return (
    <div className={styles.stepBody}>
      <div className={styles.stepSection}>
        <div className={styles.stepSectionTitle}><Globe size={14} /> Target Environment</div>
        <div className={styles.envCards}>
          {ENV_ORDER.map(env => (
            <button key={env}
              className={`${styles.envCard} ${w.environment === env ? styles.envCardActive : ''}`}
              style={w.environment === env ? { borderColor: ENV_COLORS[env], background: `${ENV_COLORS[env]}18` } : {}}
              onClick={() => set({ environment: env })}
            >
              <span className={styles.envCardDot} style={{ background: ENV_COLORS[env] }} />
              <span className={styles.envCardLabel}>{env.toUpperCase()}</span>
              {env === 'uat' || env === 'prod'
                ? <Shield size={11} className={styles.envCardLock} aria-label="Requires approval" />
                : null}
            </button>
          ))}
        </div>
        {(w.environment === 'uat' || w.environment === 'prod') && (
          <div className={styles.warningBox}>
            <TriangleAlert size={14} />
            <span>Deployment to <strong>{w.environment.toUpperCase()}</strong> requires manual approval from a Platform Admin before it proceeds.</span>
          </div>
        )}
      </div>

      <div className={styles.stepSection}>
        <div className={styles.stepSectionTitle}>
          <Settings size={14} /> Environment Variables & Secrets
          <button className={styles.addVarBtn} onClick={addVar}><Plus size={12} /> Add Variable</button>
        </div>
        <div className={styles.envVarList}>
          <div className={styles.envVarHeader}>
            <span>Key</span><span>Value</span><span>Secret</span><span />
          </div>
          {w.envVars.map((v, i) => (
            <div key={i} className={styles.envVarRow}>
              <input placeholder="VARIABLE_NAME" value={v.key}
                onChange={e => updateVar(i, { key: e.target.value })} />
              <input placeholder="value" value={v.value} type={v.secret ? 'password' : 'text'}
                onChange={e => updateVar(i, { value: e.target.value })} />
              <label className={styles.secretToggle}>
                <input type="checkbox" checked={v.secret}
                  onChange={e => updateVar(i, { secret: e.target.checked })} />
                <Shield size={12} />
              </label>
              <button className={styles.removeVarBtn} onClick={() => removeVar(i)}><X size={13} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Step5Review({ w, onDeploy }: { w: WizardState; onDeploy: () => void }) {
  const repoName = w.repoUrl.split('/').pop()?.replace('.git', '') || w.repoUrl || '—'
  return (
    <div className={styles.stepBody}>
      <div className={styles.reviewGrid}>
        <div className={styles.reviewCard}>
          <div className={styles.reviewCardTitle}><Github size={13} /> Source</div>
          <div className={styles.reviewRow}><span>Repository</span><strong>{repoName}</strong></div>
          <div className={styles.reviewRow}><span>Branch</span><strong>{w.branch}</strong></div>
          {w.subfolder && <div className={styles.reviewRow}><span>Subfolder</span><strong>{w.subfolder}</strong></div>}
          <div className={styles.reviewRow}><span>App Type</span><strong>{APP_TYPE_LABELS[w.appType]}</strong></div>
        </div>

        <div className={styles.reviewCard}>
          <div className={styles.reviewCardTitle}><Cloud size={13} /> Target</div>
          <div className={styles.reviewRow}><span>Platform</span><strong>{w.target === 'container-apps' ? 'Azure Container Apps' : 'AKS'}</strong></div>
          {w.target === 'aks' && <div className={styles.reviewRow}><span>Cluster</span><strong>{w.aksCluster || '—'}</strong></div>}
          {w.target === 'container-apps' && <div className={styles.reviewRow}><span>Region</span><strong>{w.region}</strong></div>}
          <div className={styles.reviewRow}><span>Replicas</span><strong>{w.minReplicas} – {w.maxReplicas}</strong></div>
          {w.target === 'container-apps' && <div className={styles.reviewRow}><span>CPU / Mem</span><strong>{w.cpuCores} vCPU / {w.memoryGb} GiB</strong></div>}
        </div>

        <div className={styles.reviewCard}>
          <div className={styles.reviewCardTitle}><Database size={13} /> Database</div>
          <div className={styles.reviewRow}><span>Mode</span><strong>{w.dbOption === 'new' ? 'New PostgreSQL Flexible Server' : 'Existing Database'}</strong></div>
          {w.dbOption === 'new' && <>
            <div className={styles.reviewRow}><span>Server</span><strong>{w.dbServerName || '—'}</strong></div>
            <div className={styles.reviewRow}><span>SKU</span><strong>{w.dbSku.split('(')[0].trim()}</strong></div>
            <div className={styles.reviewRow}><span>Storage</span><strong>{w.dbStorageGb} GiB</strong></div>
            <div className={styles.reviewRow}><span>HA</span><strong>{w.dbHighAvailability ? 'Yes' : 'No'}</strong></div>
          </>}
          {w.dbOption === 'existing' && <>
            <div className={styles.reviewRow}><span>Host</span><strong>{w.dbHost || '—'}</strong></div>
            <div className={styles.reviewRow}><span>Database</span><strong>{w.dbName || '—'}</strong></div>
          </>}
        </div>

        <div className={styles.reviewCard}>
          <div className={styles.reviewCardTitle}><Globe size={13} /> Environment</div>
          <div className={styles.reviewRow}><span>Target Env</span><EnvBadge env={w.environment} /></div>
          <div className={styles.reviewRow}><span>Variables</span><strong>{w.envVars.length} configured</strong></div>
          <div className={styles.reviewRow}><span>Secrets</span><strong>{w.envVars.filter(v => v.secret).length} secret(s)</strong></div>
        </div>
      </div>

      <div className={styles.deployActions}>
        <div className={styles.deployInfo}>
          <Zap size={14} />
          <span>This will provision all required Azure resources, build and push the container image to ACR, and deploy to <strong>{w.environment.toUpperCase()}</strong>. Estimated time: 3–8 minutes.</span>
        </div>
        <button className={styles.deployBtn} onClick={onDeploy}>
          <Rocket size={16} /> Deploy to {w.environment.toUpperCase()}
        </button>
      </div>
    </div>
  )
}

// ─── Deployment Progress ──────────────────────────────────────────────────────

const DEPLOY_STAGES = [
  'Cloning repository',
  'Detecting application type',
  'Building container image',
  'Pushing to Azure Container Registry',
  'Provisioning PostgreSQL Flexible Server',
  'Storing secrets in Key Vault',
  'Provisioning Azure resources',
  'Deploying application',
  'Running health checks',
  'Deployment complete',
]

function DeployingView({ appName, onDone }: { appName: string; onDone: () => void }) {
  const [stage, setStage] = useState(0)

  useState(() => {
    const t = setInterval(() => {
      setStage(s => {
        if (s >= DEPLOY_STAGES.length - 1) { clearInterval(t); return s }
        return s + 1
      })
    }, 1200)
    return () => clearInterval(t)
  })

  const done = stage >= DEPLOY_STAGES.length - 1

  return (
    <div className={styles.deployingView}>
      <div className={styles.deployingHeader}>
        {done ? <CheckCircle size={36} className={styles.deployDone} /> : <div className={styles.deploySpinner} />}
        <h2>{done ? 'Deployment Successful!' : `Deploying ${appName}…`}</h2>
        <p>{done ? 'Your application is live and health checks passed.' : 'Provisioning Azure resources and deploying your application.'}</p>
      </div>
      <div className={styles.stageList}>
        {DEPLOY_STAGES.map((s, i) => (
          <div key={s} className={`${styles.stageItem} ${i < stage ? styles.stageDone : ''} ${i === stage && !done ? styles.stageActive : ''}`}>
            {i < stage || (i === stage && done)
              ? <CheckCircle size={15} />
              : i === stage
              ? <div className={styles.stagePulse} />
              : <div className={styles.stageCircle} />
            }
            <span>{s}</span>
          </div>
        ))}
      </div>
      {done && (
        <button className={styles.deployBtn} onClick={onDone}><Eye size={15} /> View Application</button>
      )}
    </div>
  )
}

// ─── Application List ─────────────────────────────────────────────────────────

function AppRow({ app }: { app: Application }) {
  const nextEnv = ENV_ORDER[ENV_ORDER.indexOf(app.environment) + 1]
  return (
    <tr className={styles.tableRow}>
      <td>
        <div className={styles.appName}>{app.name}</div>
        <div className={styles.appRepo}><Github size={11} /> {app.repo}</div>
      </td>
      <td><TargetBadge target={app.target} /></td>
      <td><EnvBadge env={app.environment} /></td>
      <td><StatusBadge status={app.status} /></td>
      <td className={styles.versionCell}>{app.version}</td>
      <td className={styles.dateCell}>{app.lastDeployed}</td>
      <td>
        <div className={styles.rowActions}>
          {nextEnv && (
            <button className={styles.promoteBtn} title={`Promote to ${nextEnv.toUpperCase()}`}>
              <ArrowUpRight size={13} /> {nextEnv.toUpperCase()}
            </button>
          )}
          <button className={styles.actionIconBtn} title="Rollback"><RotateCcw size={13} /></button>
          <button className={styles.actionIconBtn} title="Logs"><Eye size={13} /></button>
          <button className={styles.actionIconBtn} title="Settings"><Settings size={13} /></button>
        </div>
      </td>
    </tr>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type PageView = 'list' | 'wizard' | 'deploying'

export function OneClickDeployPage() {
  const [view, setView]       = useState<PageView>('list')
  const [step, setStep]       = useState(1)
  const [wizard, setWizard]   = useState<WizardState>(DEFAULT_WIZARD)
  const [apps, setApps]       = useState<Application[]>(MOCK_APPS)
  const [deployingApp, setDeployingApp] = useState('')

  function patchWizard(p: Partial<WizardState>) { setWizard(w => ({ ...w, ...p })) }

  function startWizard() { setWizard(DEFAULT_WIZARD); setStep(1); setView('wizard') }

  function deploy() {
    const repoName = wizard.repoUrl.split('/').pop()?.replace('.git', '') || 'my-app'
    setDeployingApp(repoName)
    setView('deploying')
  }

  function deployDone() {
    const repoName = wizard.repoUrl.split('/').pop()?.replace('.git', '') || 'my-app'
    const newApp: Application = {
      id: String(Date.now()), name: repoName, repo: wizard.repoUrl.replace('https://', ''),
      branch: wizard.branch, target: wizard.target, environment: wizard.environment,
      status: 'healthy', appType: wizard.appType, region: wizard.region,
      lastDeployed: 'Just now', image: `acreastus.azurecr.io/${repoName}`, version: 'v1.0.0',
    }
    setApps(a => [newApp, ...a])
    setView('list')
  }

  const healthy   = apps.filter(a => a.status === 'healthy').length
  const deploying = apps.filter(a => a.status === 'deploying').length
  const failed    = apps.filter(a => a.status === 'failed').length

  // ── Wizard view ──
  if (view === 'wizard') return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Rocket size={20} /></div>
          <div>
            <h1 className={styles.pageTitle}>New Deployment</h1>
            <p className={styles.pageSubtitle}>One-click app deploy from GitHub to Azure</p>
          </div>
        </div>
        <button className={styles.cancelBtn} onClick={() => setView('list')}><X size={15} /> Cancel</button>
      </div>

      {/* Stepper */}
      <div className={styles.stepper}>
        {STEPS.map(({ n, label, Icon }) => (
          <div key={n} className={`${styles.stepperItem} ${step === n ? styles.stepperActive : ''} ${step > n ? styles.stepperDone : ''}`}>
            <div className={styles.stepperCircle} onClick={() => step > n && setStep(n)}>
              {step > n ? <CheckCircle size={14} /> : <Icon size={14} />}
            </div>
            <span className={styles.stepperLabel}>{label}</span>
            {n < STEPS.length && <ChevronRight size={14} className={styles.stepperArrow} />}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className={styles.wizardBody}>
        {step === 1 && <Step1 w={wizard} set={patchWizard} />}
        {step === 2 && <Step2 w={wizard} set={patchWizard} />}
        {step === 3 && <Step3 w={wizard} set={patchWizard} />}
        {step === 4 && <Step4 w={wizard} set={patchWizard} />}
        {step === 5 && <Step5Review w={wizard} onDeploy={deploy} />}
      </div>

      {/* Nav buttons */}
      {step < 5 && (
        <div className={styles.wizardNav}>
          {step > 1
            ? <button className={styles.backBtn} onClick={() => setStep(s => s - 1)}>← Back</button>
            : <span />
          }
          <button className={styles.nextBtn} onClick={() => setStep(s => Math.min(s + 1, 5) as typeof step)}>
            {step === 4 ? 'Review' : 'Next'} <ArrowRight size={15} />
          </button>
        </div>
      )}
    </div>
  )

  // ── Deploying view ──
  if (view === 'deploying') return (
    <div className={styles.page}>
      <DeployingView appName={deployingApp} onDone={deployDone} />
    </div>
  )

  // ── List view ──
  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Rocket size={20} /></div>
          <div>
            <h1 className={styles.pageTitle}>One Click App Deploy</h1>
            <p className={styles.pageSubtitle}>Deploy any GitHub repository to Azure in minutes</p>
          </div>
        </div>
        <button className={styles.newDeployBtn} onClick={startWizard}>
          <Plus size={15} /> New Deployment
        </button>
      </div>

      {/* KPI strip */}
      <div className={styles.kpiStrip}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiIcon} style={{ background: 'var(--aptean-bg-elevated)', color: 'var(--aptean-teal)' }}><Rocket size={18} /></div>
          <div><div className={styles.kpiValue}>{apps.length}</div><div className={styles.kpiLabel}>Total Apps</div></div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiIcon} style={{ background: '#16a34a18', color: 'var(--aptean-success)' }}><CheckCircle size={18} /></div>
          <div><div className={styles.kpiValue}>{healthy}</div><div className={styles.kpiLabel}>Healthy</div></div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiIcon} style={{ background: '#f59e0b18', color: 'var(--aptean-warning)' }}><Clock size={18} /></div>
          <div><div className={styles.kpiValue}>{deploying}</div><div className={styles.kpiLabel}>Deploying</div></div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiIcon} style={{ background: '#ef444418', color: 'var(--aptean-error)' }}><AlertCircle size={18} /></div>
          <div><div className={styles.kpiValue}>{failed}</div><div className={styles.kpiLabel}>Failed</div></div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiIcon} style={{ background: 'var(--aptean-bg-elevated)', color: 'var(--aptean-teal)' }}><Activity size={18} /></div>
          <div><div className={styles.kpiValue}>{apps.filter(a => a.target === 'container-apps').length}</div><div className={styles.kpiLabel}>Container Apps</div></div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiIcon} style={{ background: 'var(--aptean-bg-elevated)', color: 'var(--aptean-teal)' }}><Container size={18} /></div>
          <div><div className={styles.kpiValue}>{apps.filter(a => a.target === 'aks').length}</div><div className={styles.kpiLabel}>AKS Deployments</div></div>
        </div>
      </div>

      {/* Applications table */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <span className={styles.tableTitle}>Applications <span className={styles.countBadge}>{apps.length}</span></span>
          <button className={styles.refreshBtn}><RefreshCw size={13} /></button>
        </div>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Application</th>
                <th>Platform</th>
                <th>Environment</th>
                <th>Status</th>
                <th>Version</th>
                <th>Last Deploy</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {apps.map(app => <AppRow key={app.id} app={app} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
